import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { getArticleAuthor } from "../lib/authors";
import NotFound from "./NotFound";
import { seoTitle, seoDescription } from "../lib/seo";
import { ArticleHeader, ArticleAside, RelatedPosts } from "../components/article/ArticleParts";

const slugify = (t) =>
  t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60);

// Legacy palette baked into stored article HTML → Neo tokens (display only, DB untouched).
const LEGACY_COLORS = [[/#4caf50/gi, "#D4A63A"], [/#1e3a5f/gi, "#0F1B2D"], [/#f8fafc/gi, "#FFFFFF"]];

// Sanitize body, add ids to <h2> and collect them for the table of contents.
function buildBody(content) {
  const html = LEGACY_COLORS.reduce((h, [re, c]) => h.replace(re, c), DOMPurify.sanitize(content || ""));
  if (typeof window === "undefined" || !window.DOMParser) return { html, toc: [] };
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const toc = [];
  doc.querySelectorAll("h2").forEach((h, i) => {
    const text = h.textContent.trim();
    if (!text) return;
    const id = h.id || `${slugify(text) || "section"}-${i}`;
    h.id = id;
    toc.push({ id, text });
  });
  return { html: doc.body.firstChild.innerHTML, toc };
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useDocumentMeta({
    title: post
      ? seoTitle(post.title)
      : err
      ? "Article introuvable — FOD by DMB Partners"
      : "Article — FOD by DMB Partners",
    description: seoDescription(post?.excerpt) || "Article FOD by DMB Partners sur l'optimisation des frais bancaires.",
    canonical: `https://www.fodbydmb.com/post/${slug}`,
    // Use the article's cover image for social sharing (Facebook, LinkedIn, X, iMessage).
    // Falls back to the site logo only if the admin didn't upload an image.
    image: post?.image_url || undefined,
    imageAlt: post?.title,
    // Soft-404: if the article isn't found, tell crawlers not to index this URL
    noindex: err,
    type: post ? "article" : undefined,
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/blog/${slug}`),
      api.get(`/blog`),
    ])
      .then(([curr, all]) => {
        setPost(curr.data);
        setSuggestions(all.data.filter((p) => p.slug !== slug).slice(0, 3));
      })
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const body = useMemo(() => buildBody(post?.content), [post]);

  if (loading) {
    return (
      <div className="flex justify-center py-32">
        <Loader2 className="text-navy animate-spin" size={32} />
      </div>
    );
  }

  if (err || !post) return <NotFound />;

  // Plain-text version of article body — used both for wordCount and articleBody in schema
  const plainBody = (post.content || "")
    .replace(/<style[^>]*>.*?<\/style>/gis, " ")
    .replace(/<script[^>]*>.*?<\/script>/gis, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&amp;|&quot;|&#39;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const wordCount = plainBody ? plainBody.split(/\s+/).length : 0;
  const author = getArticleAuthor(slug, post.author);
  const date = new Date(post.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="article-page">
      <script
        type="application/ld+json"
        data-testid="article-jsonld"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image_url ? [post.image_url] : ["https://www.fodbydmb.com/og-image.png"],
            "datePublished": post.created_at,
            "dateModified": post.updated_at || post.created_at,
            "author": {
              "@type": "Person",
              "name": author.name,
              "url": author.url,
              "jobTitle": author.jobTitle,
              "worksFor": {
                "@type": "Organization",
                "name": "FOD by DMB Partners",
                "url": "https://www.fodbydmb.com",
              },
            },
            "publisher": {
              "@type": "Organization",
              "name": "FOD by DMB Partners",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.fodbydmb.com/logo.png",
              },
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.fodbydmb.com/post/${slug}`,
            },
            "articleSection": post.category || "Blog",
            "articleBody": plainBody.slice(0, 5000),
            "wordCount": wordCount,
            "keywords": (post.tags || []).join(", "),
            "inLanguage": "fr-FR",
            "isAccessibleForFree": true,
          })
            // SEC-004: escape '<' so an admin-crafted title containing
            // "</script>" cannot break out of this inline script tag.
            .replace(/</g, "\\u003c"),
        }}
      />
      <ArticleHeader post={post} author={author} date={date} />
      <div className="mx-auto max-w-[1180px] px-4 md:px-8 lg:px-12 pt-8 md:pt-12 lg:pt-16 pb-12 md:pb-16 lg:pb-[88px] flex flex-wrap gap-x-16 gap-y-10 items-start">
        <article className="flex-[999_1_560px] min-w-0 max-w-[740px] flex flex-col gap-[22px]">
          {post.image_url && (
            <img src={post.image_url} alt={post.title} className="w-full aspect-video object-cover rounded-[14px]" data-testid="article-cover" />
          )}
          {post.excerpt && (
            <p className="m-0 text-[17px] md:text-[19px] leading-[1.7] font-medium text-navy" data-testid="article-excerpt">{post.excerpt}</p>
          )}
          <div className="w-[120px] h-1.5 bg-gold" />
          <div className="prose-fod" data-testid="article-body" dangerouslySetInnerHTML={{ __html: body.html }} />
        </article>
        <ArticleAside toc={body.toc} />
      </div>
      <RelatedPosts posts={suggestions} />
    </div>
  );
}
